/**
 * PrintExportDialog — modal de configuração antes de exportar o monitor em PDF.
 * Permite escolher tema (claro/escuro), número de colunas (1 ou 2) e quais gráficos
 * incluir. Ao confirmar, devolve um objeto de config via onConfirm.
 */
import React, { useEffect, useState } from 'react';
import { Printer, Sun, Moon, Columns2, Square } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';

const ToggleButton = ({ active, onClick, icon: Icon, label }) => (
  <Button
    type="button"
    variant={active ? 'default' : 'outline'}
    size="sm"
    onClick={onClick}
    className="flex-1 justify-center gap-2"
  >
    {Icon ? <Icon className="h-4 w-4" /> : null}
    <span>{label}</span>
  </Button>
);

const PrintExportDialog = ({
  open,
  onClose,
  availableCharts = [],
  defaultSelectedCharts = [],
  defaultTheme = 'light',
  defaultColumns = 2,
  onConfirm,
}) => {
  const [theme, setTheme] = useState(defaultTheme);
  const [columns, setColumns] = useState(defaultColumns);
  const [selected, setSelected] = useState(() => new Set(defaultSelectedCharts));

  // Reset ao reabrir, alinhando com o estado padrão atual.
  useEffect(() => {
    if (open) {
      setTheme(defaultTheme);
      setColumns(defaultColumns);
      setSelected(new Set(defaultSelectedCharts));
    }
  }, [open, defaultTheme, defaultColumns, defaultSelectedCharts]);

  const toggleChart = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleConfirm = () => {
    // Preserva a ordem original de availableCharts ao devolver.
    const charts = availableCharts.map(c => c.id).filter(id => selected.has(id));
    onConfirm?.({ theme, columns, charts });
  };

  const handleOpenChange = (next) => { if (!next) onClose?.(); };

  const nothingSelected = selected.size === 0;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Printer className="h-5 w-5" />
            Exportar relatório em PDF
          </DialogTitle>
          <DialogDescription>
            Página A4 paisagem com os indicadores e gráficos selecionados.
            O navegador abre o diálogo de impressão — escolha “Salvar como PDF”.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {/* Tema */}
          <div className="space-y-2">
            <p className="text-sm font-medium">Tema</p>
            <div className="flex gap-2">
              <ToggleButton
                active={theme === 'light'}
                onClick={() => setTheme('light')}
                icon={Sun}
                label="Claro"
              />
              <ToggleButton
                active={theme === 'dark'}
                onClick={() => setTheme('dark')}
                icon={Moon}
                label="Escuro"
              />
            </div>
          </div>

          {/* Colunas */}
          <div className="space-y-2">
            <p className="text-sm font-medium">Colunas de gráficos por página</p>
            <div className="flex gap-2">
              <ToggleButton
                active={columns === 1}
                onClick={() => setColumns(1)}
                icon={Square}
                label="1 coluna"
              />
              <ToggleButton
                active={columns === 2}
                onClick={() => setColumns(2)}
                icon={Columns2}
                label="2 colunas"
              />
            </div>
          </div>

          {/* Gráficos */}
          <div className="space-y-2">
            <div className="flex items-baseline justify-between">
              <p className="text-sm font-medium">Gráficos a incluir</p>
              <div className="flex gap-3 text-xs">
                <button
                  type="button"
                  className="text-muted-foreground hover:text-foreground"
                  onClick={() => setSelected(new Set(availableCharts.map(c => c.id)))}
                >
                  Marcar todos
                </button>
                <button
                  type="button"
                  className="text-muted-foreground hover:text-foreground"
                  onClick={() => setSelected(new Set())}
                >
                  Limpar
                </button>
              </div>
            </div>
            {availableCharts.length === 0 ? (
              <p className="text-xs text-muted-foreground">
                Nenhum gráfico disponível para o período selecionado.
              </p>
            ) : (
              <div className="grid grid-cols-2 gap-x-3 gap-y-2 rounded-md border border-border p-3">
                {availableCharts.map(c => (
                  <label
                    key={c.id}
                    htmlFor={`print-chart-${c.id}`}
                    className="flex items-center gap-2 text-sm cursor-pointer select-none"
                  >
                    <Checkbox
                      id={`print-chart-${c.id}`}
                      checked={selected.has(c.id)}
                      onCheckedChange={() => toggleChart(c.id)}
                    />
                    <span>{c.label}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onClose?.()}>
            Cancelar
          </Button>
          <Button type="button" onClick={handleConfirm} disabled={nothingSelected}>
            <Printer className="h-4 w-4 mr-2" />
            Gerar PDF
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PrintExportDialog;
